import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { useList } from "../../pages/admin/AdminApp";
import { tr } from "../../lib/i18n";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Fold } from "./Section";
import { Field, I18nInput } from "./Fields";
import { IconPicker } from "./IconPicker";
import { DEFAULT_BUTTONS } from "../site/HeroButtons";

const sel = "w-full border rounded-md h-9 px-3 text-sm bg-white";
const SECTIONS = [["disiplinler", "Ne Yaptım? bölümü"], ["videolar", "Video galerisi"], ["yazilar", "Son yazılar"], ["iletisim", "İletişim bölümü"]];
const ROUTES = [["news", "Yazılar sayfası"], ["videos", "Videolar sayfası"], ["gallery", "Resim galerisi"], ["contact", "İletişim sayfası"], ["home", "Anasayfa"]];
const VARIANTS = [["primary", "Dolu (altın)"], ["secondary", "Koyu"], ["outline", "Çerçeveli"], ["ghost", "Sade yazı"]];

function Target({ b, upd, i }) {
  const { data: pages = [] } = useList("pages");
  return (
    <div className="grid md:grid-cols-[200px_1fr] gap-3">
      <Field label="Bağlantı türü">
        <select className={sel} value={b.kind || "route"} onChange={(e) => upd({ kind: e.target.value, value: "" })} data-testid={`hero-btn-kind-${i}`}>
          <option value="section">Anasayfadaki bir bölüm</option><option value="route">Sitedeki bir sayfa</option>
          <option value="page">Oluşturduğum sayfa</option><option value="url">Özel bağlantı (URL)</option>
        </select>
      </Field>
      <Field label="Hedef">
        {b.kind === "url" ? <Input value={b.value || ""} onChange={(e) => upd({ value: e.target.value })} placeholder="https://… veya /yol" data-testid={`hero-btn-url-${i}`} /> : (
          <select className={sel} value={b.value || ""} onChange={(e) => upd({ value: e.target.value })} data-testid={`hero-btn-target-${i}`}>
            <option value="">Seçin…</option>
            {(b.kind === "section" ? SECTIONS : b.kind === "page" ? pages.map((p) => [p.slug, tr(p.title)]) : ROUTES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}
      </Field>
    </div>
  );
}

export function HeroButtonsEditor({ value, onChange }) {
  const list = (value || DEFAULT_BUTTONS).map((b, i) => (b.id ? b : { ...b, id: `b${i}-${Date.now()}` }));
  const upd = (i) => (patch) => onChange(list.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i, d) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  return (
    <Field label="Butonlar">
      <div className="space-y-3" data-testid="hero-buttons-editor">
        {list.map((b, i) => (
          <Fold key={value ? b.id : i} title={`${tr(b.label) || "Yeni buton"}${b.hidden ? " (gizli)" : ""}`} meta={VARIANTS.find(([k]) => k === b.variant)?.[1]} testid={`hero-btn-${i}`}>
            <div className="flex items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-xs text-stone-600"><Switch checked={!b.hidden} onCheckedChange={(v) => upd(i)({ hidden: !v })} data-testid={`hero-btn-visible-${i}`} />Sitede göster</label>
              <div className="flex gap-1">
                <Button type="button" size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`hero-btn-up-${i}`}><ArrowUp size={14} /></Button>
                <Button type="button" size="icon" variant="ghost" disabled={i === list.length - 1} onClick={() => move(i, 1)} data-testid={`hero-btn-down-${i}`}><ArrowDown size={14} /></Button>
                <Button type="button" size="icon" variant="ghost" onClick={() => onChange(list.filter((_, j) => j !== i))} data-testid={`hero-btn-delete-${i}`}><Trash2 size={14} className="text-red-500" /></Button>
              </div>
            </div>
            <I18nInput label="Buton yazısı" value={b.label} onChange={(v) => upd(i)({ label: v.tr !== b.label?.tr && v.en && v.en === b.label?.en && DEFAULT_BUTTONS.some((d) => d.label.en === v.en) ? { ...v, en: "" } : v })} testid={`hero-btn-label-${i}`} maxLength={40} />
            <Target b={b} upd={upd(i)} i={i} />
            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Stil"><select className={sel} value={b.variant || "primary"} onChange={(e) => upd(i)({ variant: e.target.value })} data-testid={`hero-btn-variant-${i}`}>{VARIANTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
              <Field label="İkon"><IconPicker value={b.icon || "arrow-right"} onChange={(v) => upd(i)({ icon: v })} testid={`hero-btn-icon-${i}`} /></Field>
              <Field label="İkon konumu"><select className={sel} value={b.icon_pos || "start"} onChange={(e) => upd(i)({ icon_pos: e.target.value })} data-testid={`hero-btn-iconpos-${i}`}><option value="start">Yazının solunda</option><option value="end">Yazının sağında</option><option value="none">İkon yok</option></select></Field>
            </div>
            {b.kind !== "section" && <label className="flex items-center gap-2 text-xs text-stone-600"><Switch checked={!!b.new_tab} onCheckedChange={(v) => upd(i)({ new_tab: v })} data-testid={`hero-btn-newtab-${i}`} />Yeni sekmede aç</label>}
          </Fold>
        ))}
        {list.length < 4 && <Button type="button" variant="outline" size="sm" onClick={() => onChange([...list, { id: `b${Date.now()}`, label: { tr: "" }, kind: "route", value: "", variant: "secondary", icon: "arrow-right", icon_pos: "end" }])} data-testid="hero-btn-add"><Plus size={14} className="mr-1" />Buton ekle</Button>}
      </div>
    </Field>
  );
}
